import { observer } from 'mobx-react-lite'
import React, { type MouseEventHandler } from 'react'
import {
  type ITextFieldBaseAsideAction,
  makeTextFieldError,
  TextFieldPlaceholder,
} from 'shared/ui/TextField'
import { Alert, Box, Icon, type ITooltipProps, Switch, Tooltip, Typography } from 'shared/ui'
import { RcsEmptyFallbackInboxAlert } from 'entities/Rcs/ui'
import { EnrollmentPreview } from 'widgets/ContactEnrollment/ui/EnrollmentList'
import { type EnrollmentFilters } from 'widgets/ContactEnrollment/models/EnrollmentFilters'
import { SendFrom, type SendFromStore } from 'widgets/SendFrom'
import styles from './styles.module.scss'

export type IEnrolmentStepProps = {
  tooltipLabel?: ITooltipProps['label']
  enrollmentFilters: EnrollmentFilters
  error?: string | null
  onClick: MouseEventHandler<HTMLLabelElement>
  errorPlacement?: ITooltipProps['placement']
  title?: string
  numberStore?: SendFromStore
  dropdownWidth?: number
  numberError?: string
  removeError?: () => void
  rightActions?: ITextFieldBaseAsideAction[]
  contactReEnroll?: {
    value: boolean
    onChange: (value: boolean) => void
    isShowAlert?: boolean
    onCloseAlert?: () => void
    showRcsEmptyFallbackInboxAlert?: boolean
  } | null
}

export const EnrolmentStep = observer(
  ({
    tooltipLabel,
    enrollmentFilters,
    error,
    onClick,
    errorPlacement = 'bottom',
    title = 'Enroll Your Contacts',
    numberStore,
    dropdownWidth,
    numberError,
    removeError,
    rightActions,
    contactReEnroll,
  }: IEnrolmentStepProps) => {
    return (
      <div className={styles.container}>
        <span className={styles.title}>
          {title}
          {tooltipLabel && (
            <Tooltip label={tooltipLabel} placement={'top'} breakWord>
              <Icon icon={'question'} color={'var(--content-neutral-primary)'} />
            </Tooltip>
          )}
        </span>
        <Box display='flex' flexDirection='column' gap={1}>
          {numberStore && (
            <SendFrom
              store={numberStore}
              triggerVariant={'textField'}
              textFiledTriggerProps={{
                error: {
                  message: numberError,
                  placement: 'bottom',
                  anchor: 'container',
                  offset: [0, 0],
                },
                rightActions,
                textFieldPlaceholderProps: {
                  size: 'medium',
                  className: styles.input,
                },
              }}
              onClearError={() => removeError?.()}
              width={dropdownWidth}
            />
          )}
          {contactReEnroll?.showRcsEmptyFallbackInboxAlert && (
            <RcsEmptyFallbackInboxAlert alertProps={{ marginTop: 16, marginBottom: 32 }} />
          )}
          <TextFieldPlaceholder
            className={styles.placeholder}
            variant='outline'
            size='small'
            onClick={onClick}
            placeholder={
              <div className={styles.preview}>
                {!!enrollmentFilters.allSize ? (
                  <EnrollmentPreview filters={enrollmentFilters} />
                ) : (
                  'Enroll contacts (optional)'
                )}
              </div>
            }
            rightActions={[
              makeTextFieldError({
                placement: errorPlacement,
                anchor: 'container',
                offset: [0, 0],
              }),
            ]}
            error={error}
          />
        </Box>
        {contactReEnroll && (
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography
              variant={'text-input-field-r'}
              ariaLabel={'re-enter'}
              color={'var(--content-primary-secondary)'}
            >
              Allow contacts to re-enter this scenario after completion
            </Typography>
            <Switch value={contactReEnroll.value} onChange={contactReEnroll.onChange} />
          </Box>
        )}
        {contactReEnroll?.isShowAlert && (
          <Alert
            item={{
              type: 'warning',
              title: 'Contacts may re-enroll repeatedly',
              desc: 'Consider adjusting your filter or turning off re-enrollment',
            }}
            onClose={contactReEnroll.onCloseAlert}
            className={styles.contactsReEnrollAlert}
          />
        )}
      </div>
    )
  }
)
